/**
 * One line on what a standing email said (TMMRSL-0046, TMMAPI-0100), from its `facts` — each set
 * only where it applies to the email's kind. A missing fact reads as the dash. A charge email lists
 * each charge in its own currency, never summed.
 */
import type { TFunction } from 'i18next';
import type { ResellerNotice } from '@/api/resellerBilling';
import { moneyIn } from '@/components/moneyIn';
import { formatCivilMonthOrDash, formatCivilOrDash } from '@/i18n/format';

export function noticeSummary(notice: ResellerNotice, t: TFunction, locale: string): string {
  const dash = t('statements.unavailable');
  const { facts } = notice;
  const money = (minor: number | null, currency: string) =>
    minor === null ? dash : moneyIn(minor, currency, locale, dash);
  const date = (value: string | null) => formatCivilOrDash(value, locale, dash);
  const month = (value: string | null) => formatCivilMonthOrDash(value, locale, dash);

  switch (notice.kind) {
    case 'LAUNCH_REMINDER':
      return t('notices.summary.launch', { date: date(facts.launchOn) });
    case 'PROBATION_STARTED':
      return t('notices.summary.probationStarted', {
        month: month(facts.month),
        amount: money(facts.shortfallMinor, facts.currency),
      });
    case 'PROBATION_MONTHLY':
      return t('notices.summary.probationMonthly', {
        amount: money(facts.stillNeededMinor, facts.currency),
        date: date(facts.deactivationOn),
      });
    case 'CHARGE_FAILED':
    case 'CHARGE_ACTION_REQUIRED': {
      const list = facts.charges.map((charge) => money(charge.amountMinor, charge.currency));
      return t('notices.summary.charges', { list: list.length > 0 ? list.join(', ') : dash });
    }
    case 'DEACTIVATED':
      return t('notices.summary.deactivated');
    case 'RECOVERED':
      return t('notices.summary.recovered');
    default:
      return dash;
  }
}
